import clsx from 'clsx'

import { ArrowUpRight, ChevronRight, Folder } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { MatterDetailsJurisdiction } from '@/matter/components/matter-overview/MatterDetailsJurisdiction'
import type { MatterRead } from '@/matter/store/schemas'
import { canEditMatter } from '@/matter/utils/matter-permissions'
import { ContextRailDriveSyncBar } from '@/matter/components/matter-overview/ContextRailDriveSyncBar'
import { ContextRailDriveSyncStatus } from '@/matter/components/matter-overview/context-rail-drive-sync-status'
import type { ExternalStorageProvider } from '@/constants/constants-external-storage'

type ContextRailProps = {
  matter: MatterRead
  /** Null where no DMS provider is configured at all, which drops the drive-sync bar. */
  driveSyncStatus: ContextRailDriveSyncStatus | null
  /** Provider named by the drive-sync bar. Null when no account is connected. */
  provider: ExternalStorageProvider | null
  /** Full synced folder path, shown on hovering the drive-sync bar. */
  syncedFolderPath?: string | null
  /** Whether this user has permission to reconnect. */
  canReconnect?: boolean
  syncedFileCount?: number | null
  uploadedFileCount?: number | null
  onEditMatterDetails: () => void
  onOpenMatterFiles: () => void
  /** Opens the edit-matter modal scoped to jurisdiction, for the missing-jurisdiction CTA. */
  onEditJurisdiction: () => void
  /** Reconnect, connect a drive, pick a folder, or open the synced connection — whichever the status calls for. */
  onDriveSyncAction: () => void
}

export function ContextRail({
  matter,
  driveSyncStatus,
  provider,
  syncedFolderPath,
  canReconnect,
  syncedFileCount,
  uploadedFileCount,
  onEditMatterDetails,
  onOpenMatterFiles,
  onEditJurisdiction,
  onDriveSyncAction,
}: ContextRailProps) {
  const description = matter.description
  const canEdit = canEditMatter(matter)
  const hasFileCounts = typeof syncedFileCount === 'number' && typeof uploadedFileCount === 'number'

  const fileCountLine = hasFileCounts ? `${syncedFileCount.toLocaleString('en-US')} synced · ${uploadedFileCount.toLocaleString('en-US')} uploaded` : null

  return (
    <section aria-label="Matter context" className="flex w-full flex-col overflow-clip rounded-lg">
      <div
        className={clsx(
          'flex flex-col border-x border-t border-brand-neutral-200 bg-brand-neutral-50',
          driveSyncStatus === null ? 'rounded-lg border-b' : 'rounded-t-lg'
        )}
      >
        <div className="flex flex-col gap-1 border-b border-brand-neutral-200 p-4">
          <div className="flex items-center gap-1">
            <h2 className="font-rethink-sans text-sm font-medium leading-5 text-brand-neutral-950">Matter Details</h2>
            <span className="flex-1" />
            {canEdit && (
              <IconButton
                variant="plain"
                color="neutral"
                size="sm"
                icon={<ArrowUpRight />}
                aria-label="Edit matter details"
                tooltipText="Edit matter details"
                onClick={onEditMatterDetails}
              />
            )}
          </div>
          <div className="flex flex-col items-start gap-2">
            <MatterDetailsJurisdiction matter={matter} onEdit={onEditJurisdiction} />
            {/* Descriptions are unbounded, so the rail would otherwise grow past the composer it
                sits beside. Capped at eight lines, scrolling past that. */}
            {description && (
              <p className="max-h-40 min-h-0 overflow-y-auto text-sm font-normal leading-5 tracking-[-0.15px] text-brand-neutral-800">{description}</p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onOpenMatterFiles}
          className="flex flex-col gap-1 p-4 text-left transition-colors hover-capable:hover:bg-brand-neutral-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
        >
          <span className="flex min-h-7 w-full items-center gap-2">
            <Folder className="size-4 shrink-0 text-brand-neutral-950" aria-hidden="true" />
            <span className="min-w-0 flex-1 font-rethink-sans text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-950">Matter Files</span>
            <span className="flex size-7 shrink-0 items-center justify-center">
              <ChevronRight className="size-4 text-brand-neutral-950" aria-hidden="true" />
            </span>
          </span>
          {fileCountLine && <span className="min-w-0 truncate text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-600">{fileCountLine}</span>}
        </button>
      </div>

      {driveSyncStatus !== null && (
        <ContextRailDriveSyncBar
          status={driveSyncStatus}
          provider={provider}
          syncedFolderPath={syncedFolderPath}
          canReconnect={canReconnect}
          onAction={onDriveSyncAction}
        />
      )}
    </section>
  )
}
